import { useSelector, useDispatch } from 'react-redux'
import { useState, useCallback } from 'react'
import { setDraggedItem, setHoveredSlot, clearDragState, selectDraggedItem, selectHoveredSlot } from '../inventorySlice'

export const useDragDrop = () => {
  const dispatch = useDispatch()
  const draggedItem = useSelector(selectDraggedItem)
  const hoveredSlot = useSelector(selectHoveredSlot)
  const [dragPreview, setDragPreview] = useState(null)

  // Iniciar drag
  const handleDragStart = useCallback(
    (e, item, sourceType, sourceIndex) => {
      const dragData = {
        item,
        sourceType, // 'inventory' | 'equipment'
        sourceIndex,
        slotType: sourceType === 'equipment' ? sourceIndex : null,
      }

      dispatch(setDraggedItem(dragData))

      // Configurar el drag preview
      e.dataTransfer.effectAllowed = 'move'
      e.dataTransfer.setData('text/plain', JSON.stringify(dragData))

      // Crear imagen de preview personalizada
      if (item.image) {
        const img = new Image()
        img.src = item.image
        img.onload = () => {
          e.dataTransfer.setDragImage(img, 25, 25)
        }
      }

      setDragPreview(dragData)
    },
    [dispatch]
  )

  // Drag over (permitir drop)
  const handleDragOver = useCallback(
    (e, targetType, targetIndex) => {
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'

      const hoverData = {
        targetType, // 'inventory' | 'equipment'
        targetIndex,
        slotType: targetType === 'equipment' ? targetIndex : null,
      }

      dispatch(setHoveredSlot(hoverData))
    },
    [dispatch]
  )

  // Drag enter
  const handleDragEnter = useCallback((e, targetType, targetIndex) => {
    e.preventDefault()
  }, [])

  // Drag leave
  const handleDragLeave = useCallback(
    (e) => {
      // Solo limpiar si realmente salimos del elemento
      if (!e.currentTarget.contains(e.relatedTarget)) {
        dispatch(setHoveredSlot(null))
      }
    },
    [dispatch]
  )

  // Drop
  const handleDrop = useCallback(
    (e, targetType, targetIndex, onEquipItem, onUnequipItem, onMoveItem, canEquipItemInSlot, onSwapEquipment) => {
      e.preventDefault()

      try {
        const dragDataString = e.dataTransfer.getData('text/plain')
        const dragData = JSON.parse(dragDataString)

        if (!dragData || !dragData.item) {
          console.warn('No hay datos de drag válidos')
          return
        }

        const { item, sourceType, sourceIndex, slotType: sourceSlotType } = dragData
        const targetSlotType = targetType === 'equipment' ? targetIndex : null

        // Evitar drop en el mismo lugar
        if (sourceType === targetType && sourceIndex === targetIndex) {
          return
        }

        // Lógica de drop según el tipo de origen y destino
        if (sourceType === 'inventory' && targetType === 'equipment') {
          // Equipar item desde inventario
          if (canEquipItemInSlot(item, targetSlotType)) {
            onEquipItem(item, targetSlotType, sourceIndex)
          } else {
            console.warn(`El item ${item.name} no puede ser equipado en el slot ${targetSlotType}`)
          }
        } else if (sourceType === 'equipment' && targetType === 'inventory') {
          // Desequipar item al inventario
          onUnequipItem(sourceSlotType)
        } else if (sourceType === 'equipment' && targetType === 'equipment') {
          if (canEquipItemInSlot(item, targetSlotType) && onSwapEquipment) {
            onSwapEquipment(sourceSlotType, targetSlotType)
          }
        } else if (sourceType === 'inventory' && targetType === 'inventory') {
          // Mover item dentro del inventario
          onMoveItem(sourceIndex, targetIndex)
        }
      } catch (error) {
        console.error('Error procesando drop:', error)
      } finally {
        // Limpiar estado de drag
        dispatch(clearDragState())
        setDragPreview(null)
      }
    },
    [dispatch]
  )

  // Finalizar drag (cuando se cancela o termina)
  const handleDragEnd = useCallback(() => {
    dispatch(clearDragState())
    setDragPreview(null)
  }, [dispatch])

  // Verificar si un slot puede recibir el item que se está arrastrando
  const canDropInSlot = useCallback(
    (targetType, targetIndex, canEquipItemInSlot) => {
      if (!draggedItem) return false

      const { item, sourceType, sourceIndex } = draggedItem
      const targetSlotType = targetType === 'equipment' ? targetIndex : null

      // No permitir drop en el mismo lugar
      if (sourceType === targetType && sourceIndex === targetIndex) {
        return false
      }

      // Verificar compatibilidad de tipos
      if (targetType === 'equipment') {
        return canEquipItemInSlot(item, targetSlotType)
      }

      // Siempre se puede mover al inventario
      if (targetType === 'inventory') {
        return true
      }

      return false
    },
    [draggedItem]
  )

  // Verificar si un slot está siendo hover
  const isSlotHovered = useCallback(
    (targetType, targetIndex) => {
      if (!hoveredSlot) return false
      return hoveredSlot.targetType === targetType && hoveredSlot.targetIndex === targetIndex
    },
    [hoveredSlot]
  )

  // Verificar si un slot está siendo dragged
  const isSlotDragged = useCallback(
    (sourceType, sourceIndex) => {
      if (!draggedItem) return false
      return draggedItem.sourceType === sourceType && draggedItem.sourceIndex === sourceIndex
    },
    [draggedItem]
  )

  // Obtener clases CSS para el estado de drag
  const getDragClasses = useCallback(
    (type, index, canEquipItemInSlot) => {
      const classes = []

      if (isSlotDragged(type, index)) {
        classes.push('dragging')
      }

      if (isSlotHovered(type, index)) {
        if (canDropInSlot(type, index, canEquipItemInSlot)) {
          classes.push('drag-over-valid')
        } else {
          classes.push('drag-over-invalid')
        }
      }

      return classes.join(' ')
    },
    [isSlotDragged, isSlotHovered, canDropInSlot]
  )

  return {
    // Estado
    draggedItem,
    hoveredSlot,
    dragPreview,

    // Handlers de eventos
    handleDragStart,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    handleDragEnd,

    // Helpers
    canDropInSlot,
    isSlotHovered,
    isSlotDragged,
    getDragClasses,
  }
}
